/**
 * TEA Calculator Service
 *
 * Performs Techno-Economic Analysis using baked-in cost_per_unit values
 * from the template JSON. Separate from LCA for modularity.
 *
 * Cost sources: Each LCI input has a `cost_per_unit` and `cost_unit` field
 * baked into the template. These are material/energy costs only — labor,
 * overhead, and capital costs are not included.
 *
 * Sub-inventory: If an input has a `sub_inventory` field, additional per-unit
 * cost from the linked template is added on top of the base cost_per_unit.
 * The resolvedSubInventories map is provided by the caller (pre-resolved).
 *
 * Formula: cost = Σ (quantity × (cost_per_unit + sub_inv_cost)) per process
 */

import { getSubInventoryCost } from './subInventoryResolver';
import { computeFUNormalizer } from './functionalUnit';
import { readCostProvenance, summarizeCostProvenance, CONFIDENCE } from './costProvenance';

/**
 * Run TEA analysis using baked-in cost_per_unit.
 * @param {object} project - The project data
 * @param {object} resolvedSubInventories - Pre-resolved sub-inventory data (optional)
 * Returns cost breakdown per process and total.
 */
export function runTemplateTEA(project, resolvedSubInventories = {}) {
  if (!project || !project.processes) {
    return { success: false, error: 'No project data' };
  }

  const processes = project.processes;
  const scalingFactor = project.project_info?.scaling_factor || 1.0;
  const lifeExtension = project.project_info?.battery_life_extension || 1.0;
  const norm = computeFUNormalizer(project);
  // Single finalization divisor: life extension (per service-life) × energy (per kWh).
  const finalDivisor = (lifeExtension > 1.0 ? lifeExtension : 1.0) * norm.divisor;

  const processBreakdown = {};
  const provenanceEntries = [];
  let totalCost = 0;
  let totalRevenue = 0;

  // Build a map of inputs that are produced internally by an upstream process
  // (linked via next_process_ids). These should not have their purchase cost
  // re-counted in the downstream process — the upstream process already
  // accounts for the production cost of that material.
  // Map structure: { downstreamProcessId: Set<inputName> }
  const internallyProduced = {};
  for (const [upId, upProc] of Object.entries(processes)) {
    for (const downId of (upProc.next_process_ids || [])) {
      if (!processes[downId]) continue;
      const downInputs = processes[downId].lci_inputs || [];
      for (const product of (upProc.products || upProc.output_materials || [])) {
        const prodNameLower = (product.material || product.name || '').trim().toLowerCase();
        if (!prodNameLower) continue;
        for (const inp of downInputs) {
          if ((inp.name || '').trim().toLowerCase() === prodNameLower) {
            if (!internallyProduced[downId]) internallyProduced[downId] = new Set();
            internallyProduced[downId].add(inp.name);
          }
        }
      }
    }
  }

  for (const [procId, proc] of Object.entries(processes)) {
    const inputs = proc.lci_inputs || [];
    let procCost = 0;
    const costItems = [];

    // -- Revenue from outputs with a `price_per_unit` set ----------------------
    // Optional, additive: outputs without a price contribute zero revenue.
    const outputs = proc.products || proc.output_materials || [];
    let procRevenue = 0;
    const revenueItems = [];
    for (const out of outputs) {
      const price = Number(out?.price_per_unit) || 0;
      if (price <= 0) continue;
      const outQty = (Number(out?.quantity) || 0) * scalingFactor;
      const itemRev = outQty * price;
      procRevenue += itemRev;
      revenueItems.push({
        name: (out.material || out.name || 'Output').replace(/_/g, ' '),
        quantity: outQty,
        unit: out.unit || '',
        price_per_unit: price,
        total_revenue: itemRev,
      });
    }

    for (const input of inputs) {
      const qty = (input.quantity || 0) * scalingFactor;
      // base_quantity: for sub-inventory inputs, base material was produced at original qty
      const baseQty = (input.base_quantity != null ? input.base_quantity : input.quantity || 0) * scalingFactor;

      // If this input is produced by an upstream process in the project, its cost is
      // already captured there — skip the purchase price to avoid double-counting.
      const isInternallyProduced = internallyProduced[procId]?.has(input.name) ?? false;

      const baseCost = isInternallyProduced ? 0 : (input.cost_per_unit || 0);
      const subInvCost = isInternallyProduced ? 0 : getSubInventoryCost(input, resolvedSubInventories);
      // Base cost scales with baseQty (oxide production), sub-inv cost scales with qty (coated powder)
      const itemCost = (baseQty * baseCost) + (qty * subInvCost);
      procCost += itemCost;

      const item = {
        name: input.name,
        quantity: qty,
        unit: input.unit,
        cost_per_unit: isInternallyProduced ? 0 : (input.cost_per_unit || 0),
        cost_unit: input.cost_unit || 'USD/unit',
        total_cost: itemCost,
        internally_produced: isInternallyProduced || undefined,
      };
      if (subInvCost > 0) {
        item.base_cost_per_unit = baseCost;
        item.base_quantity = baseQty;
        item.sub_inventory_cost_per_unit = subInvCost;
        item.sub_inventory_template = input.sub_inventory?.template_id;
      }
      costItems.push(item);

      // Track where this money came from. Internally-produced inputs contribute
      // no purchase cost, so they carry no provenance either.
      if (!isInternallyProduced) {
        const provenance = readCostProvenance(input);
        item.cost_source = provenance.source || undefined;
        item.cost_confidence = provenance.confidence;
        if (baseQty * baseCost > 0) {
          provenanceEntries.push({ ...provenance, cost: baseQty * baseCost });
        }
        if (qty * subInvCost > 0) {
          // Priced by another template; its own inputs' provenance is not resolved here.
          provenanceEntries.push({
            source: input.sub_inventory?.template_id || 'linked template',
            year: '',
            confidence: CONFIDENCE.LINKED,
            cost: qty * subInvCost,
          });
        }
      }
    }

    processBreakdown[procId] = {
      process_name: proc.name || procId,
      total_cost: procCost,
      items: costItems,
      revenue: procRevenue,
      revenue_items: revenueItems,
      profit: procRevenue - procCost,
    };

    totalCost += procCost;
    totalRevenue += procRevenue;
  }

  // Apply the finalization divisor (life extension × energy normalization) to all
  // costs and revenue. Same deep-divide that life extension used previously.
  if (finalDivisor !== 1.0) {
    totalCost /= finalDivisor;
    totalRevenue /= finalDivisor;
    for (const pb of Object.values(processBreakdown)) {
      pb.total_cost /= finalDivisor;
      pb.items.forEach(item => { item.total_cost /= finalDivisor; });
      pb.revenue /= finalDivisor;
      pb.profit = pb.revenue - pb.total_cost;
      pb.revenue_items.forEach(it => { it.total_revenue /= finalDivisor; });
    }
  }

  const totalProfit = totalRevenue - totalCost;
  const hasRevenue = totalRevenue > 0;
  // Shares, not absolutes, so the summary survives the FU divisor below.
  const costProvenance = summarizeCostProvenance(provenanceEntries);

  const noteParts = [];
  if (norm.perKwh) noteParts.push('per kWh');
  if (lifeExtension > 1.0) noteParts.push(`${lifeExtension}× life extension`);
  const basisNote = noteParts.length ? ` (${noteParts.join(', ')})` : '';

  return {
    success: true,
    mode: 'template',
    scaling_factor: scalingFactor,
    battery_life_extension: lifeExtension,
    functional_unit_basis: norm.basis,
    energy_kwh: norm.perKwh ? norm.kwh : null,
    energy_density_wh_per_kg: norm.whPerKg ?? null,
    fu_fallback: norm.fallback || false,
    total_cost: totalCost,
    total_revenue: totalRevenue,
    total_profit: totalProfit,
    has_revenue: hasRevenue,
    cost_unit: norm.perKwh ? 'USD/kWh' : 'USD',
    cost_provenance: costProvenance,
    process_breakdown: processBreakdown,
    note: `Material/energy costs only (excludes labor, overhead, capital)${basisNote}`,
  };
}

export default { runTemplateTEA };
